import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetSkillSubCategories } from '../../hooks/use-skill-sub-categories';
import { useGetSkills } from '../../hooks/use-skills';
import { DEFAULT_SKILLS_ORDER } from '../../graphql/queries';
import { SkillSubCategoriesPagingResult } from '../../types/skill-sub-category.types';
import { Skill, SkillsPagingResult } from '../../types/skill.types';
import { SkillFormDialog } from '../skill-form-dialog/skill-form-dialog';
import { createSkillTableColumns } from '../skill-table-columns/skill-table-columns';

const ALL_SUB_CATEGORIES = 'all';
/** Every sub-category for the dropdowns; the lookups elsewhere in the app use the same size. */
const SUB_CATEGORY_LOOKUP_SIZE = 1000;

/** "Skills" tab of the Skill Manager: the skills HR offers on profiles, each under one sub-category. */
export const SkillTab = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.SKILLS_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.SKILLS_UPDATE);

  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SKILLS_ORDER,
  });
  const [subCategoryFilter, setSubCategoryFilter] = useState(ALL_SUB_CATEGORIES);
  const [formState, setFormState] = useState<{ open: boolean; skill: Skill | null }>({
    open: false,
    skill: null,
  });

  const { data: subCategoryData } = useGetSkillSubCategories({
    pageNo: 1,
    pageSize: SUB_CATEGORY_LOOKUP_SIZE,
  });
  const subCategories = useMemo(
    () =>
      (
        (subCategoryData as { SkillSubCategorys?: SkillSubCategoriesPagingResult })
          ?.SkillSubCategorys?.items ?? []
      ).map(({ ItemId, Title }) => ({ ItemId, Title })),
    [subCategoryData]
  );
  const subCategoryTitles = useMemo(
    () => Object.fromEntries(subCategories.map((item) => [item.ItemId, item.Title])),
    [subCategories]
  );

  const { data, isLoading, error } = useGetSkills({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(
      subCategoryFilter === ALL_SUB_CATEGORIES ? {} : { SubCategoryId: subCategoryFilter },
      columnWhere
    ),
    order,
  });
  const result = (data as { Skills?: SkillsPagingResult })?.Skills;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const handleFilterChange = (value: string) => {
    setSubCategoryFilter(value);
    resetPage();
  };

  const columns = createSkillTableColumns({
    t,
    subCategoryTitles,
    onEdit: (skill) => setFormState({ open: true, skill }),
    canUpdate,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <Select value={subCategoryFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-full sm:w-56" aria-label={t('SUB_CATEGORY')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SUB_CATEGORIES}>{t('ALL_SUB_CATEGORIES')}</SelectItem>
            {subCategories.map((subCategory) => (
              <SelectItem key={subCategory.ItemId} value={subCategory.ItemId}>
                {subCategory.Title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canCreate && (
          <Button
            className="w-full sm:w-auto"
            onClick={() => setFormState({ open: true, skill: null })}
          >
            <Plus className="size-4" />
            {t('CREATE_SKILL')}
          </Button>
        )}
      </div>

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <SkillFormDialog
        open={formState.open}
        onOpenChange={(open) => setFormState((prev) => ({ open, skill: open ? prev.skill : null }))}
        skill={formState.skill}
        subCategories={subCategories}
      />
    </div>
  );
};
